import { Suspense } from "react"
import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { zodResolver } from "@hookform/resolvers/zod"
import { TrashSimple } from "@phosphor-icons/react"
import { useUser } from "@/apis/bff"
import { useDirtyDrawerMaker } from "@/components/drawer-wrapper"
import { Button } from "@/components/ui/button"
import { DetailCheckbox } from "@/components/ui/checkbox"
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { FormField } from "@/components/ui/form"
import { TranslatedFieldError } from "@/components/ui/form-field-error"
import { Input, TextArea } from "@/components/ui/input"
import { RadioGroup, RadioGroupItem, RadioGroupLabel } from "@/components/ui/radio-group"
import {
  LabelAndSelectValue,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { withErrorBoundary } from "@/hoc/with-error-boundary"
import { useToast } from "@/hooks/useToast"
import { cn, preventTextInput } from "@/lib/utils"
import { textAreaDefaultMaxLength } from "@/models/common"
import { CareProviderSearch } from "@/modules/care-provider/simple-search/care-provider-search"
import { DiagnosisCode } from "@/modules/patients/components/diagnosis-code"
import { LastModificationInfo } from "@/modules/patients/detail/last-modification-info"
import { MedicalHistory } from "@/modules/patients/detail/medical-history"
import { useLatestDiagnosisCode } from "@/modules/patients/detail/progress-note/hooks/useLatestDiagnosisCode"
import {
  AIMS_OF_TREATMENT,
  CONDITION_STAGES,
  OCCURRENCES,
  PRESCRIPTION_REPETITIONS,
  REASONS,
  SIDES,
  TREATMENT_UNITS_PER_DAY,
} from "@/modules/patients/detail/therapy-prescription/ch/constants"
import {
  Physiotherapy,
  PhysiotherapySchema,
} from "@/modules/patients/detail/therapy-prescription/ch/schema/physiotherapy-schema"
import { chPhysiotherapyFragment$key } from "@/modules/patients/detail/therapy-prescription/ch/services/__generated__/chPhysiotherapyFragment.graphql"
import { chPhysiotherapyFragment } from "@/modules/patients/detail/therapy-prescription/ch/services/chPhysiotherapyFragment"
import { useChCreatePhysiotherapyMutation } from "@/modules/patients/detail/therapy-prescription/ch/services/useChCreatePhysiotherapyMutation"
import { useChDeletePhysiotherapyMutation } from "@/modules/patients/detail/therapy-prescription/ch/services/useChDeletePhysiotherapyMutation"
import { useChGeneratePhysiotherapyDocumentMutation } from "@/modules/patients/detail/therapy-prescription/ch/services/useChGeneratePhysiotherapyDocumentMutation"
import { useChUpdatePhysiotherapyMutation } from "@/modules/patients/detail/therapy-prescription/ch/services/useChUpdatePhysiotherapyMutation"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import {
  DOCUMENT_LANGUAGES,
  getDocumentLanguageLabel,
  useDefaultDocumentLanguage,
} from "@/modules/patients/hooks/useDefaultDocumentLanguage"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { PatientAllergiesOutputFragment$key } from "@/modules/patients/services/__generated__/PatientAllergiesOutputFragment.graphql"
import { PatientConditionInformationOutputFragment$key } from "@/modules/patients/services/__generated__/PatientConditionInformationOutputFragment.graphql"
import { PatientAllergiesOutputFragment } from "@/modules/patients/services/PatientAllergiesOutputFragment"
import { PatientConditionInformationOutputFragment } from "@/modules/patients/services/PatientConditionInformationOutputFragment"
import { usePatientByIdQuery } from "@/modules/patients/services/usePatientByIdQuery"
import { useConfirmStore } from "@/stores/useConfirmStore"
import { useDrawerStore } from "@/stores/useDrawerStore"

interface ChPhysiotherapyDrawerProps extends FetchKeyProps {
  progressNoteId: string
  id?: string
  physiotherapyRef?: chPhysiotherapyFragment$key
}

const ChPhysiotherapyDrawer = withErrorBoundary(
  ({ physiotherapyRef, progressNoteId, id, fetchKey }: ChPhysiotherapyDrawerProps) => {
    const user = useUser()
    const hasSavePermission = user.isOneOf(["Doctor", "MedicalSupervisor"])
    const { t } = useTranslation()
    const { toast } = useToast()
    const closeDrawer = useDrawerStore((state) => state.closeDrawer)
    const [createPhysiotherapy, isCreating] = useChCreatePhysiotherapyMutation()
    const [updatePhysiotherapy, isUpdating] = useChUpdatePhysiotherapyMutation()
    const [deletePhysiotherapy] = useChDeletePhysiotherapyMutation()
    const [generateChPhysiotherapyDocument, isGenerating] = useChGeneratePhysiotherapyDocumentMutation()
    const includeEnglishDocument = false
    const { documentLanguage } = useDefaultDocumentLanguage()
    const confirm = useConfirmStore((state) => state.confirm)
    const cancel = useConfirmStore((state) => state.cancel)
    const patientId = usePatientParam()
    const isEditMode = !!id
    const isAddMode = !isEditMode && patientId && progressNoteId
    const persistedData = useFragment(chPhysiotherapyFragment, physiotherapyRef ?? null)
    const updatedAt = persistedData?.updatedAt || persistedData?.createdAt
    const updatedBy = persistedData?.updatedBy || persistedData?.createdBy
    const { diagnosisCode: latestDiagnosisCode } = useLatestDiagnosisCode(progressNoteId)
    const countryCode = useCountryParam()
    const appointmentId = useAppointmentParam()
    const [patientData] = usePatientByIdQuery(patientId, countryCode, appointmentId)
    const { conditionInformation: conditionInformationRef } = patientData.patientById!
    const { conditions: conditionList } = useFragment<PatientConditionInformationOutputFragment$key>(
      PatientConditionInformationOutputFragment,
      conditionInformationRef,
    )
    const { allergiesInformation: allergiesInformationRef } = patientData.patientById!
    const { allergies: allergyList } = useFragment<PatientAllergiesOutputFragment$key>(
      PatientAllergiesOutputFragment,
      allergiesInformationRef,
    )

    const parsedData = isEditMode
      ? PhysiotherapySchema.parse({
          ...persistedData,
          treatmentUnit: persistedData?.treatmentUnit.toString(),
          leadingSymptom: persistedData?.leadingSymptom ?? "",
          trigger: persistedData?.trigger ?? "",
          duration: persistedData?.duration ?? "",
          internalNote: persistedData?.internalNote ?? "",
          healthcareProviderId: persistedData?.healthcareProviderId ?? "",
        })
      : null

    const form = useForm<Physiotherapy>({
      mode: "all",
      resolver: zodResolver(PhysiotherapySchema),
      defaultValues:
        isEditMode && parsedData
          ? {
              ...parsedData,
            }
          : {
              language: documentLanguage,
              prescriptionRepetition: "FIRST",
              aims: [],
              reason: "SICKNESS",
              treatmentUnit: "9",
              treatmentUnitPerDay: "ONCE",
              isHomeTreatment: false,
              occurrence: "FIRST_OCCURRENCE",
              sides: [],
              conditionStage: "ACUTE",
              diagnosisCode: latestDiagnosisCode,
              conditions: [],
              allergies: [],
              medicationIds: [],
            },
    })

    const {
      setValue,
      register,
      control,
      formState: { isDirty, errors },
    } = form

    useDirtyDrawerMaker(isDirty)

    const handleCloseDrawer = () => {
      closeDrawer()
      FaroInsightsWrapper.pushEvent("documents_physiotherapy_close")
    }

    const onSubmit: SubmitHandler<Physiotherapy> = (data) => {
      if (!progressNoteId) return

      const formattedData = {
        ...data,
        treatmentUnit: Number(data.treatmentUnit),
        leadingSymptom: data.leadingSymptom || null,
        trigger: data.trigger || null,
        duration: data.duration || null,
        internalNote: data.internalNote || null,
        healthcareProviderId: data.healthcareProviderId || null,
      }

      if (isAddMode) {
        createPhysiotherapy({
          variables: {
            input: {
              language: formattedData.language,
              healthcareProviderId: formattedData.healthcareProviderId,
              treatmentUnit: formattedData.treatmentUnit,
              prescriptionRepetition: formattedData.prescriptionRepetition,
              aims: formattedData.aims,
              reason: formattedData.reason,
              sides: formattedData.sides,
              leadingSymptom: formattedData.leadingSymptom,
              trigger: formattedData.trigger,
              duration: formattedData.duration,
              conditionStage: formattedData.conditionStage,
              internalNote: formattedData.internalNote,
              allergies: formattedData.allergies,
              conditions: formattedData.conditions,
              medicationIds: formattedData.medicationIds,
              treatmentUnitPerDay: formattedData.treatmentUnitPerDay,
              isHomeTreatment: formattedData.isHomeTreatment,
              occurrence: formattedData.occurrence,
              patientId,
              progressNoteId,
              diagnosisCode: formattedData.diagnosisCode,
            },
          },
          onCompleted(response) {
            const id = response.ch_createPhysiotherapyPrescription.ch_PhysiotherapyPrescription?.id
            if (id) {
              handleGeneratePhysiotherapy(id, data)
            }
          },
          updater(store, data) {
            try {
              const progressNoteRecordProxy = store.get(progressNoteId)
              const physiotherapyPrescription = data?.ch_createPhysiotherapyPrescription?.ch_PhysiotherapyPrescription

              if (physiotherapyPrescription?.id) {
                const newRecordProxy = store.get(physiotherapyPrescription.id)
                if (newRecordProxy) {
                  progressNoteRecordProxy?.setLinkedRecord(newRecordProxy, "ch_physiotherapyPrescription")
                }
              }
            } catch (error) {
              console.error("Error while processing data:", error)
            }
          },
        })
      }

      if (isEditMode) {
        updatePhysiotherapy({
          variables: {
            input: {
              language: formattedData.language,
              healthcareProviderId: formattedData.healthcareProviderId,
              treatmentUnit: formattedData.treatmentUnit,
              prescriptionRepetition: formattedData.prescriptionRepetition,
              aims: formattedData.aims,
              reason: formattedData.reason,
              sides: formattedData.sides,
              leadingSymptom: formattedData.leadingSymptom,
              trigger: formattedData.trigger,
              duration: formattedData.duration,
              conditionStage: formattedData.conditionStage,
              internalNote: formattedData.internalNote,
              allergies: formattedData.allergies,
              conditions: formattedData.conditions,
              medicationIds: formattedData.medicationIds,
              treatmentUnitPerDay: formattedData.treatmentUnitPerDay,
              isHomeTreatment: formattedData.isHomeTreatment,
              occurrence: formattedData.occurrence,
              id,
              diagnosisCode: formattedData.diagnosisCode,
            },
          },
          onCompleted(response) {
            const id = response.ch_updatePhysiotherapyPrescription.ch_PhysiotherapyPrescription?.id
            if (id) {
              handleGeneratePhysiotherapy(id, data)
            }
          },
        })
      }
    }

    const onDelete = () => {
      if (progressNoteId && id) {
        deletePhysiotherapy({
          variables: {
            input: {
              id,
            },
          },
          onCompleted() {
            handleCloseDrawer()
            toast({
              description: t("patient.therapyPrescriptionPhysiotherapy.deleteMessage.success"),
            })
            cancel()
          },
        })
      }
    }

    const handleDelete = () => {
      confirm({
        title: t("patient.therapyPrescriptionPhysiotherapy.deleteConfirmTitle"),
        content: t("episodeOfCare.deleteProgressNote.content"),
        onConfirm: onDelete,
        confirmVariant: "danger",
        confirmText: t("common.button.delete"),
        cancelText: t("common.button.keep"),
      })
    }

    const handleGeneratePhysiotherapy = (id: string, data: Physiotherapy) => {
      if (id) {
        const { diagnosisCode, conditions, allergies } = data
        const conditionInfo = conditions
          ? conditionList
              .filter((item) => conditions.includes(item.icdDiagnosis.id))
              .map((item) => item.icdDiagnosis.diagnosisCode + " " + item.icdDiagnosis.text)
          : []

        const allergiesInfo = allergies
          ? allergyList
              .filter((item) => allergies.includes(item.icdDiagnosis.id))
              .map((item) => item.icdDiagnosis.diagnosisCode + " " + item.icdDiagnosis.text)
          : []

        generateChPhysiotherapyDocument({
          id,
          diagnosisCode,
          conditions: conditionInfo,
          allergies: allergiesInfo,
          onCompleted() {
            handleCloseDrawer()
          },
        })
      }
    }

    return (
      <Drawer open onOpenChange={handleCloseDrawer}>
        <FormProvider {...form}>
          <DrawerContent>
            <DrawerClose className="top-2 right-2" />
            <DrawerHeader className="pb-4" data-testid="physiotherapy-header">
              <DrawerTitle className="animate-fade-in-bottom text-on-surface-subdued text-sm font-medium">
                {t("patient.therapyPrescriptionPhysiotherapy.therapyPrescriptionPhysiotherapy")}
              </DrawerTitle>
            </DrawerHeader>
            <DrawerBody data-testid="physiotherapy-body">
              <div className="mb-8 flex flex-col gap-4">
                <FormField
                  control={control}
                  name="language"
                  render={({ field: { name, value, onChange }, formState: { errors }, fieldState: { invalid } }) => (
                    <div>
                      <Select value={value} onValueChange={onChange}>
                        <SelectTrigger hasError={invalid} className="w-56.5" data-testid="document-language-select">
                          <SelectValue asChild>
                            <LabelAndSelectValue
                              data-testid="document-language"
                              label={t("patient.therapyPrescription.documentLanguage")}
                              value={t(getDocumentLanguageLabel(value, includeEnglishDocument))}
                            />
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {DOCUMENT_LANGUAGES.map((language) => (
                            <SelectItem
                              value={language.value}
                              key={language.value}
                              data-testid={`document-language-${language.value.toLocaleLowerCase()}`}>
                              {t(language.label)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <TranslatedFieldError errors={errors} name={name} />
                    </div>
                  )}
                />
              </div>
              <div className="mb-8 flex flex-col gap-4">
                <label className="text-on-surface text-base font-medium">{t("patient.therapyPrescription.to")}</label>
                <div className="w-full">
                  <FormField
                    name="healthcareProviderId"
                    control={control}
                    render={({ field: { name, value, onChange } }) => (
                      <CareProviderSearch
                        label={t("patient.therapyPrescription.healthcareProvider")}
                        searchType="THERAPY_PRESCRIPTION_PHYSIOTHERAPY_DOCUMENT"
                        name={name}
                        value={value}
                        analyticsSectionName="documents_physiotherapy"
                        defaultAdvancedSearchFilters={{ groupType: "PHYSIOTHERAPIST" }}
                        onSelect={onChange}
                        onRemove={() => onChange("")}
                        error={errors.healthcareProviderId?.message}
                        allowUnknownCareProvider
                      />
                    )}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-4">
                <label className="text-on-surface text-base font-medium">{t("patient.therapyPrescription.for")}</label>
                <div className="flex">
                  <div className="w-2/5">
                    <Input
                      {...register("treatmentUnit")}
                      data-testid="treatment-unit-input"
                      error={errors.treatmentUnit?.message}
                      translateError
                      label={t("patient.therapyPrescription.treatmentUnits")}
                      maxLength={2}
                      required
                      onChange={(e) => {
                        setValue("treatmentUnit", preventTextInput(e.target.value), {
                          shouldValidate: true,
                          shouldDirty: true,
                        })
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-4 pt-4">
                <div>
                  <label className="text-on-surface-subdued block pb-2 text-sm">
                    {t("patient.therapyPrescriptionPhysiotherapy.treatmentUnitsPerDay")}
                  </label>
                  <div className="flex gap-4">
                    <FormField
                      control={control}
                      name="treatmentUnitPerDay"
                      render={({ field: { onChange, value } }) => (
                        <RadioGroup
                          className="flex flex-wrap"
                          onValueChange={onChange}
                          defaultValue={value}
                          value={value}
                          data-testid="treatment-units">
                          {TREATMENT_UNITS_PER_DAY.map((unit, index) => (
                            <RadioGroupItem
                              value={unit}
                              key={unit}
                              data-testid={`treatment-unit-${unit.toLowerCase()}`}>
                              <RadioGroupLabel>{++index}</RadioGroupLabel>
                            </RadioGroupItem>
                          ))}
                        </RadioGroup>
                      )}
                    />
                  </div>
                </div>
                <div>
                  <label className="text-on-surface-subdued block pb-2 text-sm">
                    {t("patient.therapyPrescriptionPhysiotherapy.prescriptionRepetition")}
                  </label>
                  <div className="flex gap-4">
                    <FormField
                      control={control}
                      name="prescriptionRepetition"
                      render={({ field: { onChange, value } }) => (
                        <RadioGroup
                          className="flex flex-wrap"
                          onValueChange={onChange}
                          defaultValue={value}
                          value={value}
                          data-testid="prescription-repetition">
                          {PRESCRIPTION_REPETITIONS.map((repetition) => (
                            <RadioGroupItem
                              value={repetition}
                              key={repetition}
                              data-testid={`prescription-repetition-${repetition.toLowerCase()}`}>
                              <RadioGroupLabel>
                                {t(`patient.therapyPrescriptionPhysiotherapy.repetitions.${repetition}`)}
                              </RadioGroupLabel>
                            </RadioGroupItem>
                          ))}
                        </RadioGroup>
                      )}
                    />
                  </div>
                </div>
                <FormField
                  control={control}
                  name="isHomeTreatment"
                  render={({ field: { value, onChange } }) => (
                    <DetailCheckbox checked={value} data-testid="home-treatment-checkbox" onCheckedChange={onChange}>
                      {t("patient.therapyPrescriptionPhysiotherapy.homeTreatment")}
                    </DetailCheckbox>
                  )}
                />
                <div>
                  <div
                    className={cn(
                      "mb-2",
                      errors.aims?.message && "outline-critical! rounded-md outline-2 outline-offset-4 outline-solid",
                    )}>
                    <label className="text-on-surface-subdued block pb-2 text-sm">
                      {t("patient.therapyPrescriptionPhysiotherapy.aimOfTheTreatement") + "*"}
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {AIMS_OF_TREATMENT.map((aim) => (
                        <FormField
                          key={aim}
                          control={control}
                          name="aims"
                          render={({ field: { value = [], onChange } }) => (
                            <DetailCheckbox
                              key={aim}
                              checked={value.includes(aim)}
                              data-testid={`${aim.toLowerCase()}-treatment-aim-checkbox`}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  onChange([...value, aim].sort((a, b) => a.localeCompare(b)))
                                } else {
                                  onChange(value.filter((value) => value !== aim))
                                }
                              }}>
                              {t(`patient.therapyPrescriptionPhysiotherapy.treatmentAims.${aim}`)}
                            </DetailCheckbox>
                          )}
                        />
                      ))}
                    </div>
                  </div>
                  <TranslatedFieldError errors={errors} name="aims" />
                </div>
              </div>
              <div className="flex flex-col gap-4 pt-8">
                <label className="text-on-surface text-base font-medium">
                  {t("patient.therapyPrescription.reason")}
                </label>
                <div className="flex gap-4">
                  <FormField
                    control={control}
                    name="reason"
                    render={({ field: { value, onChange } }) => (
                      <RadioGroup
                        className="flex flex-wrap"
                        defaultValue={value}
                        value={value}
                        data-testid="reason"
                        onValueChange={onChange}>
                        {REASONS.map((reason) => (
                          <RadioGroupItem value={reason} key={reason} data-testid={`${reason.toLowerCase()}-reason`}>
                            <RadioGroupLabel>{t(`patient.therapyPrescription.reasons.${reason}`)}</RadioGroupLabel>
                          </RadioGroupItem>
                        ))}
                      </RadioGroup>
                    )}
                  />
                </div>
                <FormField
                  control={control}
                  name="diagnosisCode"
                  render={({ field: { name, value, onChange }, fieldState: { invalid } }) => (
                    <Suspense fallback={null}>
                      <DiagnosisCode
                        defaultValue={isAddMode ? latestDiagnosisCode : persistedData?.diagnosisCode}
                        name={name}
                        errors={errors}
                        invalid={invalid}
                        value={value}
                        fetchKey={fetchKey}
                        onSelect={onChange}
                      />
                    </Suspense>
                  )}
                />
                <FormField
                  control={control}
                  name="occurrence"
                  render={({ field: { onChange, value } }) => (
                    <RadioGroup
                      className="flex flex-wrap"
                      onValueChange={onChange}
                      defaultValue={value}
                      value={value}
                      data-testid="occurrence">
                      {OCCURRENCES.map((occurrence) => (
                        <RadioGroupItem
                          value={occurrence}
                          key={occurrence}
                          data-testid={`occurrence-${occurrence.toLowerCase()}`}>
                          <RadioGroupLabel>
                            {t(`patient.therapyPrescriptionPhysiotherapy.occurrence.${occurrence}`)}
                          </RadioGroupLabel>
                        </RadioGroupItem>
                      ))}
                    </RadioGroup>
                  )}
                />
                <div>
                  <label className="text-on-surface-subdued block pb-2 text-sm">
                    {t("patient.therapyPrescription.side")}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {SIDES.map((side) => (
                      <FormField
                        key={side}
                        control={control}
                        name="sides"
                        render={({ field: { onChange, value = [] } }) => (
                          <DetailCheckbox
                            data-testid={`${side.toLowerCase()}-side-checkbox`}
                            checked={value.includes(side)}
                            onCheckedChange={(checked) => {
                              return checked
                                ? onChange([...value, side].sort((a, b) => a.localeCompare(b)))
                                : onChange(value.filter((value) => value !== side))
                            }}
                            key={side}
                            className="pr-1.25">
                            {t(`patient.therapyPrescriptionPhysiotherapy.side.${side}`)}
                          </DetailCheckbox>
                        )}
                      />
                    ))}
                  </div>
                  <div className="mt-4 flex flex-wrap gap-4">
                    <Input
                      {...register("leadingSymptom")}
                      label={t("patient.therapyPrescriptionPhysiotherapy.leadingSymtoms")}
                      data-testid="leading-symptom-input"
                      error={errors.leadingSymptom?.message}
                    />
                    <Input
                      {...register("trigger")}
                      label={t("patient.therapyPrescriptionPhysiotherapy.trigger")}
                      data-testid="trigger-input"
                      error={errors.trigger?.message}
                    />
                    <Input
                      {...register("duration")}
                      label={t("patient.therapyPrescriptionPhysiotherapy.duration")}
                      data-testid="duration-input"
                      error={errors.duration?.message}
                    />
                  </div>
                  <div className="mt-4 flex flex-wrap gap-4">
                    <FormField
                      control={control}
                      name="conditionStage"
                      render={({ field: { onChange, value } }) => (
                        <RadioGroup
                          className="flex flex-wrap"
                          onValueChange={onChange}
                          defaultValue={value}
                          value={value}>
                          {CONDITION_STAGES.map((conditionStage) => (
                            <RadioGroupItem
                              value={conditionStage}
                              key={conditionStage}
                              data-testid={`${conditionStage.toLowerCase()}-condition-stage`}>
                              <RadioGroupLabel>
                                {t(`patient.therapyPrescriptionPhysiotherapy.conditionState.${conditionStage}`)}
                              </RadioGroupLabel>
                            </RadioGroupItem>
                          ))}
                        </RadioGroup>
                      )}
                    />
                  </div>
                </div>
                <TextArea
                  {...register("internalNote")}
                  label={t("patient.therapyPrescriptionPhysiotherapy.internalNotes")}
                  data-testid="internal-note"
                  error={errors.internalNote?.message}
                  translateError
                  textLimit={textAreaDefaultMaxLength}
                />
              </div>
              <MedicalHistory />
            </DrawerBody>
            <DrawerFooter className="flex-wrap gap-2" data-testid="physiotherapy-footer">
              {isEditMode && (
                <div className="flex gap-2">
                  {updatedAt && updatedBy && <LastModificationInfo updatedAt={updatedAt} updatedBy={updatedBy} />}
                  <button
                    onClick={() => {
                      FaroInsightsWrapper.pushEvent("documents_physiotherapy_delete")
                      handleDelete()
                    }}
                    data-testid="physiotherapy-delete-button">
                    <TrashSimple className="text-on-surface-subdued" />
                  </button>
                </div>
              )}
              <div className="ml-auto flex gap-2">
                <Button data-testid="physiotherapy-cancel-button" variant="secondary" onClick={handleCloseDrawer}>
                  {t("common.button.cancel")}
                </Button>
                <Button
                  disabled={!hasSavePermission || (isEditMode && !isDirty)}
                  data-testid="physiotherapy-psychotherapy-save-button"
                  onClick={() => {
                    FaroInsightsWrapper.pushEvent("documents_physiotherapy_save")
                    form.handleSubmit(onSubmit)()
                  }}
                  isLoading={isCreating || isUpdating || isGenerating}>
                  {t("common.button.save")}
                </Button>
              </div>
            </DrawerFooter>
          </DrawerContent>
        </FormProvider>
      </Drawer>
    )
  },
  "ChPhysiotherapyDrawer",
)

export { ChPhysiotherapyDrawer }
